import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Check, Repeat } from 'lucide-react';
import { CATEGORIES, MONTH_NAMES, DAY_ABBR } from '../../constants/expenses';
import { formatCurrency } from '../../utils/formatters';

// A recurring expense can project multiple rows into the same month (e.g.
// paid on the 1st and the 15th) that all share the same underlying id, so
// selection is keyed per calendar occurrence rather than per expense id.
const occurrenceKey = (expense) => `${expense.id}|${expense.date}`;

const MonthlyView = ({
  year, month, expenses, income, loading,
  onPrev, onNext, onEdit, onIncomeChange,
}) => {
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month;
  const todayStr = today.toISOString().split('T')[0];

  const [editingIncome, setEditingIncome] = useState(false);
  const [incomeInput, setIncomeInput] = useState('');
  const incomeRef = useRef(null);

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [activeCategory, setActiveCategory] = useState(null);

  // Reset UI state when navigating months
  useEffect(() => {
    setSelectMode(false);
    setSelected(new Set());
    setActiveCategory(null);
  }, [year, month]);

  // Categories that actually appear in this month's expenses, in canonical order
  const filterCategories = CATEGORIES.filter(cat => expenses.some(e => e.category === cat));

  // Apply category filter to the expense list (footer always shows unfiltered totals)
  const visibleExpenses = activeCategory
    ? expenses.filter(e => e.category === activeCategory)
    : expenses;

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const remainingExpenses = isCurrentMonth
    ? expenses.filter(e => e.date > todayStr).reduce((sum, e) => sum + e.amount, 0)
    : 0;
  const net = income - totalExpenses;

  const selectedTotal = visibleExpenses
    .filter(e => selected.has(occurrenceKey(e)))
    .reduce((sum, e) => sum + e.amount, 0);

  const enterSelectMode = () => {
    setSelectMode(true);
    setSelected(new Set());
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const toggleSelect = (key) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openIncomeEdit = () => {
    setIncomeInput(income === 0 ? '' : String(income));
    setEditingIncome(true);
  };

  useEffect(() => {
    if (editingIncome) incomeRef.current?.focus();
  }, [editingIncome]);

  const commitIncome = () => {
    const parsed = parseFloat(incomeInput.replace(',', '.'));
    onIncomeChange(isNaN(parsed) ? 0 : parsed);
    setEditingIncome(false);
  };

  const handleIncomeKey = (e) => {
    if (e.key === 'Enter') commitIncome();
    if (e.key === 'Escape') setEditingIncome(false);
  };

  // Build interleaved rows from visibleExpenses + optional today line
  const rows = [];
  let todayInserted = false;

  for (const expense of visibleExpenses) {
    if (isCurrentMonth && !todayInserted && expense.date > todayStr) {
      rows.push({ kind: 'today' });
      todayInserted = true;
    }
    rows.push({ kind: 'expense', expense });
  }

  if (isCurrentMonth && !todayInserted) {
    rows.push({ kind: 'today' });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      {selectMode ? (
        <div className="flex items-center justify-between">
          <button
            onClick={exitSelectMode}
            className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
            aria-label="Exit selection"
          >
            <X className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-gray-900">
            {selected.size === 0 ? 'Select items' : `${selected.size} selected`}
          </span>
          <button
            onClick={() => setSelected(new Set())}
            disabled={selected.size === 0}
            className="text-sm text-gray-400 hover:text-gray-900 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors px-1.5"
          >
            Clear
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <button
            onClick={onPrev}
            className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-gray-900 font-semibold text-lg tracking-tight">
            {MONTH_NAMES[month - 1]} {year}
          </h2>
          <div className="flex items-center gap-1">
            <button
              onClick={enterSelectMode}
              className="text-sm text-gray-400 hover:text-gray-900 transition-colors px-1.5"
            >
              Select
            </button>
            <button
              onClick={onNext}
              className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-50"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Category filter bar — only shown when this month has categorised expenses */}
      {!selectMode && filterCategories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-0.5" style={{ scrollbarWidth: 'none' }}>
          <button
            onClick={() => setActiveCategory(null)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              activeCategory === null
                ? 'bg-accent text-white border-accent'
                : 'border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          {filterCategories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(c => c === cat ? null : cat)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                activeCategory === cat
                  ? 'bg-accent text-white border-accent'
                  : 'border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Expense list */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">
            {activeCategory ? `No ${activeCategory} expenses this month` : 'No expenses this month'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {rows.map((row, i) => {
              if (row.kind === 'today') {
                return (
                  <div key="today-line" className="flex items-center gap-3 px-5 py-2">
                    <span className="flex-1 h-px bg-gray-100" />
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Today</span>
                    <span className="flex-1 h-px bg-gray-100" />
                  </div>
                );
              }

              const { expense } = row;
              const d = new Date(expense.date + 'T00:00:00');
              const dayNum = d.getDate();
              const dayName = DAY_ABBR[d.getDay()];
              const isPast = expense.date < todayStr;
              const isSelected = selected.has(occurrenceKey(expense));

              return (
                <div
                  key={`${expense.id}-${i}`}
                  onClick={selectMode ? () => toggleSelect(occurrenceKey(expense)) : () => onEdit(expense)}
                  className={`flex items-center gap-3 px-5 py-3 cursor-pointer select-none transition-colors hover:bg-gray-50 ${
                    isPast && !selectMode ? 'opacity-60' : ''
                  } ${isSelected ? 'bg-emerald-50 hover:bg-emerald-50' : ''}`}
                >
                  {selectMode && (
                    <span
                      className={`w-[18px] h-[18px] rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-accent border-accent' : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </span>
                  )}
                  <span className="flex flex-col items-start w-10 flex-shrink-0">
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">{dayNum}</span>
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">{dayName}</span>
                  </span>
                  <span className="flex-1 min-w-0 flex items-center gap-1.5 text-sm font-medium text-gray-900 truncate">
                    {expense.title}
                    {expense.recurring && <Repeat className="w-3 h-3 text-gray-400 flex-shrink-0" />}
                  </span>
                  <span className="text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">
                    -{formatCurrency(expense.amount)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {selectMode ? (
        <div className="bg-gray-900 rounded-2xl px-5 py-4 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-gray-300 uppercase tracking-wide">
            {selected.size === 0 ? 'None selected' : `Selected (${selected.size})`}
          </span>
          <span className="text-white font-semibold tabular-nums">
            {selected.size === 0 ? '—' : `-${formatCurrency(selectedTotal)}`}
          </span>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card divide-y divide-gray-100 overflow-hidden">
          <div
            onClick={openIncomeEdit}
            className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Income</span>
            {editingIncome ? (
              <input
                ref={incomeRef}
                type="number"
                inputMode="decimal"
                step="0.01"
                value={incomeInput}
                onChange={(e) => setIncomeInput(e.target.value)}
                onBlur={commitIncome}
                onKeyDown={handleIncomeKey}
                placeholder="0.00"
                className="w-28 text-right text-sm font-semibold tabular-nums bg-transparent border-b border-gray-300 outline-none focus:border-accent"
              />
            ) : (
              <span className={`text-sm tabular-nums ${income === 0 ? 'text-gray-400' : 'text-gray-900 font-semibold'}`}>
                {income === 0 ? 'Tap to set' : formatCurrency(income)}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between px-5 py-3">
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Expenses</span>
            <span className="text-sm font-semibold text-gray-900 tabular-nums">-{formatCurrency(totalExpenses)}</span>
          </div>

          {isCurrentMonth && (
            <div className="flex items-center justify-between px-5 py-3">
              <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Remaining</span>
              <span className="text-sm text-gray-500 tabular-nums">-{formatCurrency(remainingExpenses)}</span>
            </div>
          )}

          <div className={`flex items-center justify-between px-5 py-3 ${net < 0 ? 'bg-rose-50' : 'bg-emerald-50'}`}>
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Net</span>
            <span className={`text-sm font-semibold tabular-nums ${net >= 0 ? 'text-accent' : 'text-negative'}`}>
              {net >= 0 ? '+' : '-'}{formatCurrency(Math.abs(net))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyView;
